import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ProgressBar } from 'react-native-paper';

import type { CollaborativeAchievement } from '../../store/slices/socialLearningAnalyticsSlice';

interface CollaborativeAchievementCardProps {
  achievement: CollaborativeAchievement;
  onCelebrate: () => void;
  onShare: () => void;
}

export const CollaborativeAchievementCard: React.FC<
  CollaborativeAchievementCardProps
> = ({ achievement, onCelebrate, onShare }) => {
  const isCompleted = achievement.progress.overallProgress >= 1;

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      default:
        return '#666';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'bronze':
        return 'medal';
      case 'silver':
        return 'medal';
      case 'gold':
        return 'trophy';
      case 'platinum':
        return 'diamond';
      default:
        return 'star';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'group_milestone':
        return 'flag';
      case 'peer_support':
        return 'people';
      case 'knowledge_sharing':
        return 'library';
      case 'collaboration_quality':
        return 'handshake';
      default:
        return 'star';
    }
  };

  const formatParticipants = (participants: string[]) => {
    if (participants.length <= 3) {
      return participants.join(', ');
    }
    return `${participants.slice(0, 2).join(', ')} and ${participants.length - 2} others`;
  };

  return (
    <View style={[styles.container, isCompleted && styles.completedContainer]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <Ionicons
            name={getTypeIcon(achievement.type) as any}
            size={24}
            color={getDifficultyColor(achievement.difficulty)}
          />
        </View>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{achievement.title}</Text>
          <View style={styles.metaInfo}>
            <View style={styles.difficultyBadge}>
              <Ionicons
                name={getDifficultyIcon(achievement.difficulty) as any}
                size={12}
                color={getDifficultyColor(achievement.difficulty)}
              />
              <Text
                style={[
                  styles.difficultyText,
                  { color: getDifficultyColor(achievement.difficulty) },
                ]}
              >
                {achievement.difficulty.toUpperCase()}
              </Text>
            </View>
            <Text style={styles.pointsText}>{achievement.points} pts</Text>
          </View>
        </View>
        {isCompleted && (
          <View style={styles.completedBadge}>
            <Ionicons name='checkmark-circle' size={24} color='#4CAF50' />
          </View>
        )}
      </View>

      {/* Description */}
      <Text style={styles.description}>{achievement.description}</Text>

      {/* Participants */}
      <View style={styles.participantsSection}>
        <Ionicons name='people-outline' size={16} color='#666' />
        <Text style={styles.participantsText}>
          {formatParticipants(achievement.participants)}
        </Text>
      </View>

      {/* Progress */}
      <View style={styles.progressSection}>
        <View style={styles.progressHeader}>
          <Text style={styles.progressLabel}>Progress</Text>
          <Text style={styles.progressPercentage}>
            {Math.round(achievement.progress.overallProgress * 100)}%
          </Text>
        </View>
        <ProgressBar
          progress={achievement.progress.overallProgress}
          color={isCompleted ? '#4CAF50' : '#2196F3'}
          style={styles.progressBar}
        />
      </View>

      {/* Requirements */}
      <View style={styles.requirementsSection}>
        <Text style={styles.requirementsTitle}>Requirements</Text>
        {achievement.requirements.slice(0, 3).map((req, index) => (
          <View key={index} style={styles.requirementItem}>
            <Ionicons
              name={req.completed ? 'checkmark-circle' : 'ellipse-outline'}
              size={16}
              color={req.completed ? '#4CAF50' : '#ccc'}
            />
            <Text
              style={[
                styles.requirementText,
                req.completed && styles.completedRequirement,
              ]}
            >
              {req.description}
            </Text>
            <Text style={styles.requirementProgress}>
              {req.current}/{req.target}
            </Text>
          </View>
        ))}
      </View>

      {/* Milestones */}
      {achievement.progress.milestones.length > 0 && (
        <View style={styles.milestonesSection}>
          <Text style={styles.milestonesTitle}>Milestones</Text>
          <View style={styles.milestonesList}>
            {achievement.progress.milestones.map((milestone, index) => (
              <View key={milestone.id} style={styles.milestoneItem}>
                <View
                  style={[
                    styles.milestoneIndicator,
                    milestone.achieved && styles.achievedMilestone,
                  ]}
                >
                  {milestone.achieved ? (
                    <Ionicons name='checkmark' size={12} color='#fff' />
                  ) : (
                    <Text style={styles.milestoneNumber}>{index + 1}</Text>
                  )}
                </View>
                <Text
                  style={[
                    styles.milestoneTitle,
                    milestone.achieved && styles.achievedMilestoneText,
                  ]}
                >
                  {milestone.title}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Next Milestone */}
      {achievement.progress.nextMilestone && !isCompleted && (
        <View style={styles.nextMilestoneSection}>
          <View style={styles.nextMilestoneHeader}>
            <Ionicons name='flag-outline' size={16} color='#FF9800' />
            <Text style={styles.nextMilestoneTitle}>Next Milestone</Text>
          </View>
          <Text style={styles.nextMilestoneDescription}>
            {achievement.progress.nextMilestone.description}
          </Text>
          <Text style={styles.estimatedCompletion}>
            Estimated completion:{' '}
            {new Date(
              achievement.progress.estimatedCompletion
            ).toLocaleDateString()}
          </Text>
        </View>
      )}

      {/* Actions */}
      <View style={styles.actionsSection}>
        {isCompleted ? (
          <>
            <TouchableOpacity
              style={styles.celebrateButton}
              onPress={onCelebrate}
            >
              <Ionicons name='sparkles' size={16} color='#fff' />
              <Text style={styles.celebrateButtonText}>Celebrate</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.shareButton} onPress={onShare}>
              <Ionicons name='share-outline' size={16} color='#2196F3' />
              <Text style={styles.shareButtonText}>Share</Text>
            </TouchableOpacity>
          </>
        ) : (
          <View style={styles.inProgressActions}>
            <Text style={styles.encouragementText}>
              Keep collaborating to unlock this achievement!
            </Text>
          </View>
        )}
      </View>

      {/* Earned Date */}
      {isCompleted && (
        <View style={styles.earnedSection}>
          <Ionicons name='calendar-outline' size={14} color='#666' />
          <Text style={styles.earnedText}>
            Earned on {new Date(achievement.earnedAt).toLocaleDateString()}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  completedContainer: {
    borderWidth: 2,
    borderColor: '#4CAF50',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
    marginBottom: 4,
  },
  metaInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  difficultyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  difficultyText: {
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 4,
  },
  pointsText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600',
  },
  completedBadge: {
    marginLeft: 8,
  },
  description: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginBottom: 12,
  },
  participantsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  participantsText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 8,
  },
  progressSection: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
    color: '#666',
  },
  progressPercentage: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f0f0f0',
  },
  requirementsSection: {
    marginBottom: 16,
  },
  requirementsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  requirementText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 8,
    flex: 1,
  },
  completedRequirement: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  requirementProgress: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600',
  },
  milestonesSection: {
    marginBottom: 16,
  },
  milestonesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  milestonesList: {
    gap: 8,
  },
  milestoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  milestoneIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#e0e0e0',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  achievedMilestone: {
    backgroundColor: '#4CAF50',
  },
  milestoneNumber: {
    fontSize: 10,
    color: '#666',
    fontWeight: '600',
  },
  milestoneTitle: {
    fontSize: 13,
    color: '#666',
  },
  achievedMilestoneText: {
    color: '#4CAF50',
    fontWeight: '600',
  },
  nextMilestoneSection: {
    backgroundColor: '#fff3e0',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  nextMilestoneHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  nextMilestoneTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FF9800',
    marginLeft: 6,
  },
  nextMilestoneDescription: {
    fontSize: 13,
    color: '#666',
    marginBottom: 4,
  },
  estimatedCompletion: {
    fontSize: 12,
    color: '#FF9800',
    fontWeight: '600',
  },
  actionsSection: {
    marginBottom: 12,
  },
  celebrateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#4CAF50',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
  },
  celebrateButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e3f2fd',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  shareButtonText: {
    color: '#2196F3',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  inProgressActions: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  encouragementText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
  },
  earnedSection: {
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    paddingTop: 12,
  },
  earnedText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 6,
  },
});
